import React, { useEffect, useMemo, useState } from "react";
import StyledModal from "../../components/CustomModal";
import { Form, FormInstance, Input, Tag, Select, Space } from "antd";
import { PlusOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectCategories } from "../../redux/features/category/categorySlice";
import { OptionProps } from "antd/es/select";
import { selectSubCategories } from "../../redux/features/subCategory/subCategorySlice";
import { FooterButtons, FormWrapper, StyledButton } from "../../components/styles";

const languageOptions = [
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "pt", label: "Portuguese" },
    { code: "ar", label: "Arabic" },
];

type ModalProps = {
    isModalOpen: boolean;
    handleSave: (v: any) => void;
    onCancel: () => void;
    form: FormInstance<any>;
    hasAddPermission: boolean,
    hasEditPermission: boolean,
    hasViewPermission: boolean
};

const CreateEditModal: React.FC<ModalProps> = ({ isModalOpen, handleSave, onCancel, form, hasAddPermission, hasEditPermission, hasViewPermission }) => {
    const dispatch = useAppDispatch();
    const [subcategories, setSubcategories] = useState<string[]>([]);
    const [editingTagIndex, setEditingTagIndex] = useState<number | null>(null);
    const [editingValue, setEditingValue] = useState<string>("");
    const [inputError, setInputError] = useState<string | null>(null);

    const { data: parentCategories, status: parentCategoryStatus } = useAppSelector(selectCategories);
    const { loading } = useAppSelector(selectSubCategories);

    const parentCategoryOptions: OptionProps[] = useMemo(() => parentCategories.map((c) => ({ label: c.name, value: c.id, children: c.name })), [parentCategories]);

    const parseTranslations = (value: any) => {
        if (!value) return {};
        if (typeof value === "string") {
            try {
                return JSON.parse(value) || {};
            } catch {
                return {};
            }
        }
        return value;
    };

    useEffect(() => {
        if (isModalOpen) {
            const record = form.getFieldsValue(true);
            form.setFieldsValue({
                translationKey: record?.translationKey || undefined,
                nameTranslations: parseTranslations(record?.nameTranslations),
            });
        }
    }, [isModalOpen, form]);

    const validateSubcategory = (value: string): boolean => {
        if (!value.trim()) {
            return false;
        }
        if (value.length < 2 || value.length > 50) {
            setInputError("Subcategory must be between 3 and 50 characters.");
            return false;
        }
        if (subcategories.includes(value)) {
            setInputError("Subcategory already exists.");
            return false;
        }
        setInputError(null);
        return true;
    };

    const handleAddTag = () => {
        if (validateSubcategory(editingValue)) {
            setSubcategories((prev) => [...prev, editingValue.trim()]);
            setEditingValue("");
        }
    };

    const handleRemoveTag = (index: number) => {
        setSubcategories((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSaveTagEdit = () => {
        if (editingTagIndex !== null && validateSubcategory(editingValue)) {
            const updatedTags = [...subcategories];
            updatedTags[editingTagIndex] = editingValue.trim();
            setSubcategories(updatedTags);
            setEditingTagIndex(null);
            setEditingValue("");
        }
    };

    const validateBeforeSave = (): boolean => {
        if (subcategories.length === 0) {
            setInputError("Please add at least one subcategory.");
            return false;
        }
        setInputError(null);
        return true;
    };

    const canSaveChanges = useMemo(() => hasAddPermission || hasEditPermission, [hasAddPermission, hasEditPermission]);
    return (
        <StyledModal
            draggable={true}
            title={form.getFieldValue("id") ? `${canSaveChanges ? "Edit" : "View"} Subcategory` : `${canSaveChanges ? "Add" : "View"} Subcategory`}
            open={isModalOpen}
            closeIcon={null}
            footer={
                <FooterButtons>
                    <StyledButton onClick={onCancel} disabled={loading}>
                        Cancel
                    </StyledButton>
                    {canSaveChanges && <StyledButton
                        loading={loading}
                        type="primary"
                        onClick={async () => {
                            try {
                                const values = await form.validateFields(); // include translation fields too
                                if (!form.getFieldValue("id")) {
                                    if (!validateBeforeSave()) return;
                                    const payload = {
                                        name: subcategories,
                                        ...values,
                                    };
                                    await handleSave(payload);
                                } else {
                                    await handleSave(values);
                                }
                                setSubcategories([]);
                                setInputError(null);
                            } catch (error) {
                                console.error("Validation failed:", error);
                            }
                        }}
                    >
                        Save
                    </StyledButton>}
                </FooterButtons>
            }
            centered
            onCancel={() => !loading && onCancel()}
        >
            <FormWrapper form={form} layout="vertical" disabled={loading || !canSaveChanges}>
                <Form.Item label="Parent Category" name="category" rules={[{ required: true, message: "Please select a parent category" }]}>
                    <Select
                        size="large"
                        disabled={loading || parentCategoryStatus === 'failed' || parentCategoryStatus === 'idle' || (!hasAddPermission && !hasEditPermission)}
                        placeholder="Select Parent Category"
                        options={parentCategoryOptions}
                        loading={parentCategoryStatus === 'loading'}
                        filterOption={(input: any, option: OptionProps | undefined) => (option ? option.label.toLowerCase().includes(input.toLowerCase()) : false)}
                    />
                </Form.Item>
                {form.getFieldValue("id") ? (
                    <>
                        <Form.Item label="Category Name" name="name" rules={[{ required: true, message: "Please enter name of subcategory" }]}>
                            <Input size="large" placeholder="Select Parent Category" disabled={loading || (!hasAddPermission && !hasEditPermission)} />
                        </Form.Item>
                        <Form.Item label="Translation Key" name="translationKey" tooltip="Optional i18n key for this sub category">
                            <Input size="large" placeholder="e.g. subcategory.laptops" disabled={loading || (!hasAddPermission && !hasEditPermission)} />
                        </Form.Item>
                        <div style={{ marginBottom: 8, fontWeight: 600 }}>Localized Names</div>
                        <Space direction="vertical" style={{ width: "100%" }}>
                            {languageOptions.map((lang) => (
                                <Form.Item key={lang.code} name={["nameTranslations", lang.code]} label={lang.label}>
                                    <Input
                                        size="large"
                                        disabled={loading || (!hasAddPermission && !hasEditPermission)}
                                        placeholder={`Enter ${lang.label} name`}
                                    />
                                </Form.Item>
                            ))}
                        </Space>
                    </>
                ) : (
                    <Form.Item label="Subcategories" required validateStatus={inputError ? "error" : ""} help={inputError}>
                        <Space direction="vertical" style={{ width: "100%" }}>
                            <Input
                                size="large"
                                disabled={loading}
                                placeholder="Add a new subcategory"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onPressEnter={handleAddTag}
                                addonAfter={<PlusOutlined style={{ cursor: "pointer" }} onClick={handleAddTag} />}
                            />
                            <div>
                                {subcategories.map((subcategory, index) => (
                                    <Tag key={index} style={{ marginBottom: 8, padding: "5px" }}>
                                        {editingTagIndex === index ? (
                                            <Input size="small" value={editingValue} onChange={(e) => setEditingValue(e.target.value)} onBlur={handleSaveTagEdit} onPressEnter={handleSaveTagEdit} autoFocus />
                                        ) : (
                                            <span onDoubleClick={() => setEditingTagIndex(index)}>{subcategory}</span>
                                        )}
                                        <CloseCircleOutlined disabled={loading} style={{ marginLeft: 8, color: "#f5222d", cursor: "pointer" }} onClick={() => !loading && handleRemoveTag(index)} />
                                    </Tag>
                                ))}
                            </div>
                        </Space>
                    </Form.Item>
                )}
                {/* <Row>
                    <Form.Item name="active" valuePropName="checked">
                        <Checkbox>Active</Checkbox>
                    </Form.Item>
                </Row> */}
            </FormWrapper>
        </StyledModal>
    );
};

export default CreateEditModal;
